import React, { useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IndexValuesResponse } from '../services/api';

interface Props {
  data: IndexValuesResponse;
  height?: number;
}

const INDEX_COLOR     = '#C9A84C'; // GSW gold — frictionless index
const PORTFOLIO_COLOR = '#5B8DB8'; // muted steel blue — tracking portfolio
const SHORTFALL_COLOR = '#D4634A'; // muted coral-red — implementation shortfall

type ChartView = 'both' | 'index' | 'portfolio' | 'shortfall';

const CHART_VIEWS: { key: ChartView; label: string }[] = [
  { key: 'both',      label: 'Both'      },
  { key: 'index',     label: 'Index'     },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'shortfall', label: 'Shortfall' },
];

interface MergedPoint {
  date: string;
  index_value: number | undefined;
  portfolio_value: number | undefined;
  rebalance_signal: boolean;
}

interface ShortfallPoint {
  date: string;
  shortfall_value: number;
  rebalance_signal: boolean;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
}

function fmtValue(v: number): string {
  return v.toFixed(2);
}

function fmtReturn(v: number): string {
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  const point: MergedPoint = payload[0].payload;
  const idxEntry = payload.find((p: any) => p.dataKey === 'index_value');
  const pfEntry  = payload.find((p: any) => p.dataKey === 'portfolio_value');
  return (
    <div className="bg-gsw-navy border border-white/20 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white/50 mb-1.5">{fmtDate(label)}</p>
      {idxEntry && idxEntry.value !== undefined && (
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-0.5 shrink-0" style={{ backgroundColor: INDEX_COLOR }} />
          <span className="font-semibold" style={{ color: INDEX_COLOR }}>{fmtValue(idxEntry.value)}</span>
          <span className="text-white/40 text-[10px]">Index</span>
        </div>
      )}
      {pfEntry && pfEntry.value !== undefined && (
        <div className="flex items-center gap-2 mt-0.5">
          <span className="inline-block w-3 h-0.5 shrink-0" style={{ backgroundColor: PORTFOLIO_COLOR }} />
          <span className="font-semibold" style={{ color: PORTFOLIO_COLOR }}>{fmtValue(pfEntry.value)}</span>
          <span className="text-white/40 text-[10px]">Portfolio</span>
        </div>
      )}
      {point.rebalance_signal && (
        <p className="mt-1 text-amber-400 text-[10px]">↺ Rebalance</p>
      )}
    </div>
  );
};

const ShortfallTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  const point: ShortfallPoint = payload[0].payload;
  const val: number = payload[0].value;
  return (
    <div className="bg-gsw-navy border border-white/20 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white/50 mb-1.5">{fmtDate(label)}</p>
      <div className="flex items-center gap-2">
        <span className="inline-block w-3 h-0.5 shrink-0" style={{ backgroundColor: SHORTFALL_COLOR }} />
        <span className="font-semibold" style={{ color: val >= 0 ? '#4ade80' : SHORTFALL_COLOR }}>
          {val >= 0 ? '+' : ''}{fmtValue(val)}
        </span>
        <span className="text-white/40 text-[10px]">pts</span>
      </div>
      {point.rebalance_signal && (
        <p className="mt-1 text-amber-400 text-[10px]">↺ Rebalance</p>
      )}
    </div>
  );
};

const REBALANCE_LINE_THRESHOLD = 8;

export default function IndexChart({ data, height = 280 }: Props) {
  const [tableView,    setTableView]    = useState<'portfolio' | 'index'>('portfolio');
  const [chartView,    setChartView]    = useState<ChartView>('both');
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  if (!data.index.length && !data.portfolio.length) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-xs dark:text-white/30 text-gray-400">No index data yet</p>
      </div>
    );
  }

  // ── Merge both series by date ────────────────────────────────────────────────
  const mergedMap = new Map<string, MergedPoint>();
  data.index.forEach((d) => {
    mergedMap.set(d.date, {
      date: d.date,
      index_value: d.index_value,
      portfolio_value: undefined,
      rebalance_signal: d.rebalance_signal,
    });
  });
  data.portfolio.forEach((d) => {
    const ex = mergedMap.get(d.date);
    if (ex) {
      ex.portfolio_value = d.index_value;
    } else {
      mergedMap.set(d.date, {
        date: d.date,
        index_value: undefined,
        portfolio_value: d.index_value,
        rebalance_signal: false,
      });
    }
  });
  const merged = Array.from(mergedMap.values()).sort((a, b) => a.date.localeCompare(b.date));

  // ── Shortfall series (portfolio − index, dates where both exist) ─────────────
  const shortfallData: ShortfallPoint[] = merged
    .filter((d) => d.index_value !== undefined && d.portfolio_value !== undefined)
    .map((d) => ({
      date: d.date,
      shortfall_value: d.portfolio_value! - d.index_value!,
      rebalance_signal: d.rebalance_signal,
    }));

  const rebalanceDates = merged.filter((d) => d.rebalance_signal).map((d) => d.date);
  const useDots = rebalanceDates.length > REBALANCE_LINE_THRESHOLD;

  // ── Summary numbers ──────────────────────────────────────────────────────────
  const lastIndex     = data.index.length    > 0 ? data.index[data.index.length - 1]          : null;
  const lastPortfolio = data.portfolio.length > 0 ? data.portfolio[data.portfolio.length - 1] : null;
  const indexCurrent     = lastIndex?.index_value    ?? null;
  const portfolioCurrent = lastPortfolio?.index_value ?? null;
  const indexReturn     = indexCurrent     !== null ? ((indexCurrent     - 1000) / 1000) * 100 : null;
  const portfolioReturn = portfolioCurrent !== null ? ((portfolioCurrent - 1000) / 1000) * 100 : null;
  const shortfall =
    indexCurrent !== null && portfolioCurrent !== null && indexCurrent > 0
      ? (portfolioCurrent / indexCurrent - 1) * 100
      : null;

  // ── Y-axis domain ────────────────────────────────────────────────────────────
  const domainValues: number[] = [];
  if (chartView === 'shortfall') {
    shortfallData.forEach((d) => domainValues.push(d.shortfall_value));
    domainValues.push(0); // zero always in range
  } else {
    if (chartView === 'both' || chartView === 'index')
      data.index.forEach((d) => domainValues.push(d.index_value));
    if (chartView === 'both' || chartView === 'portfolio')
      data.portfolio.forEach((d) => domainValues.push(d.index_value));
  }
  const minVal = domainValues.length ? Math.min(...domainValues) : 900;
  const maxVal = domainValues.length ? Math.max(...domainValues) : 1100;
  const padding = (maxVal - minVal) * 0.08 || 10;
  const yMin = Math.floor(minVal - padding);
  const yMax = Math.ceil(maxVal  + padding);

  // ── Rebalance dot renderer ───────────────────────────────────────────────────
  const rebalanceDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload.rebalance_signal) {
      return <circle key={payload.date} cx={cx} cy={cy} r={0} fill="none" />;
    }
    return (
      <circle
        key={payload.date}
        cx={cx} cy={cy} r={4}
        fill="rgba(201,168,76,0.85)"
        stroke="#0A1628"
        strokeWidth={1.5}
      />
    );
  };

  // ── Legend click: toggle series visibility (Both view only) ─────────────────
  const handleLegendClick = (entry: any) => {
    const key: string = entry.dataKey;
    if (!key) return;
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleChartViewChange = (v: ChartView) => {
    setChartView(v);
    setHiddenSeries(new Set()); // reset legend toggles on view switch
  };

  const chartData: any[] = chartView === 'shortfall' ? shortfallData : merged;
  const tableData = tableView === 'portfolio' ? data.portfolio : data.index;
  const isShortfall = chartView === 'shortfall';

  return (
    <div>
      {/* ── Summary row (always visible) ──────────────────────────────────── */}
      <div className="flex items-start gap-6 mb-4 flex-wrap">
        {indexCurrent !== null && (
          <div>
            <p className="text-[10px] dark:text-white/40 text-gray-400 uppercase tracking-wide mb-0.5">Index Value</p>
            <p className="text-2xl font-bold font-mono" style={{ color: INDEX_COLOR }}>{fmtValue(indexCurrent)}</p>
            {indexReturn !== null && (
              <p className={`text-sm font-semibold mt-0.5 ${indexReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {fmtReturn(indexReturn)}
              </p>
            )}
          </div>
        )}
        {portfolioCurrent !== null && (
          <div>
            <p className="text-[10px] dark:text-white/40 text-gray-400 uppercase tracking-wide mb-0.5">Portfolio Value</p>
            <p className="text-2xl font-bold font-mono" style={{ color: PORTFOLIO_COLOR }}>{fmtValue(portfolioCurrent)}</p>
            {portfolioReturn !== null && (
              <p className={`text-sm font-semibold mt-0.5 ${portfolioReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {fmtReturn(portfolioReturn)}
              </p>
            )}
          </div>
        )}
        {shortfall !== null && (
          <div>
            <p className="text-[10px] dark:text-white/40 text-gray-400 uppercase tracking-wide mb-0.5">Impl. Shortfall</p>
            <p className={`text-xl font-bold font-mono mt-0.5 ${shortfall >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {fmtReturn(shortfall)}
            </p>
          </div>
        )}
        <div className="ml-auto self-start">
          <p className="text-[10px] dark:text-white/40 text-gray-400 uppercase tracking-wide mb-0.5">Base</p>
          <p className="text-sm dark:text-white/60 text-gray-500 font-mono">1,000.00</p>
        </div>
      </div>

      {/* ── Chart view toggle ──────────────────────────────────────────────── */}
      <div className="flex justify-end mb-3">
        <div className="flex gap-1">
          {CHART_VIEWS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handleChartViewChange(key)}
              className={`px-3 py-1 text-[10px] rounded font-semibold uppercase tracking-wide transition ${
                chartView === key
                  ? 'bg-gsw-gold/15 border border-gsw-gold/40 text-gsw-gold'
                  : 'dark:text-white/40 text-gray-400 border border-transparent dark:hover:border-white/20 hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Chart ─────────────────────────────────────────────────────────── */}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={fmtDate}
            tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.35)' }}
            axisLine={false}
            tickLine={false}
            minTickGap={50}
          />
          <YAxis
            domain={[yMin, yMax]}
            tickFormatter={isShortfall ? (v) => v.toFixed(1) : fmtValue}
            tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.35)' }}
            axisLine={false}
            tickLine={false}
            width={isShortfall ? 64 : 56}
            label={isShortfall ? {
              value: 'Index points',
              angle: -90,
              position: 'insideLeft',
              dx: -6,
              style: { fontSize: 9, fill: 'rgba(255,255,255,0.28)', textAnchor: 'middle' },
            } : undefined}
          />
          <Tooltip content={isShortfall ? <ShortfallTooltip /> : <CustomTooltip />} />

          {/* Legend with click-to-toggle in Both view */}
          {chartView === 'both' && (
            <Legend
              verticalAlign="top"
              align="right"
              iconType="line"
              height={28}
              onClick={handleLegendClick}
              formatter={(value, entry: any) => (
                <span style={{
                  fontSize: 11,
                  cursor: 'pointer',
                  color: hiddenSeries.has(entry.dataKey)
                    ? 'rgba(255,255,255,0.2)'
                    : 'rgba(255,255,255,0.6)',
                }}>
                  {value}
                </span>
              )}
            />
          )}

          {/* Base line at 1000 — not meaningful in Shortfall view */}
          {!isShortfall && (
            <ReferenceLine y={1000} stroke="rgba(255,255,255,0.15)" strokeDasharray="4 4" />
          )}

          {/* Zero reference in Shortfall view */}
          {isShortfall && (
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" strokeDasharray="4 4" />
          )}

          {/* Rebalance vertical lines — all views */}
          {!useDots && rebalanceDates.map((d) => (
            <ReferenceLine
              key={d}
              x={d}
              stroke="rgba(201,168,76,0.4)"
              strokeWidth={1}
            />
          ))}

          {/* Index line */}
          {(chartView === 'both' || chartView === 'index') && (
            <Line
              type="monotone"
              dataKey="index_value"
              name="Index (frictionless)"
              stroke={INDEX_COLOR}
              strokeWidth={2}
              dot={useDots ? rebalanceDot : false}
              activeDot={{ r: 4, fill: INDEX_COLOR, stroke: '#0A1628', strokeWidth: 2 }}
              connectNulls={false}
              hide={hiddenSeries.has('index_value')}
            />
          )}

          {/* Portfolio line */}
          {(chartView === 'both' || chartView === 'portfolio') && (
            <Line
              type="monotone"
              dataKey="portfolio_value"
              name="Tracking Portfolio (net of costs & fees)"
              stroke={PORTFOLIO_COLOR}
              strokeWidth={2}
              dot={useDots ? rebalanceDot : false}
              activeDot={{ r: 4, fill: PORTFOLIO_COLOR, stroke: '#0A1628', strokeWidth: 2 }}
              connectNulls={false}
              hide={hiddenSeries.has('portfolio_value')}
            />
          )}

          {/* Shortfall line */}
          {isShortfall && (
            <Line
              type="monotone"
              dataKey="shortfall_value"
              name="Implementation Shortfall"
              stroke={SHORTFALL_COLOR}
              strokeWidth={2}
              dot={useDots ? rebalanceDot : false}
              activeDot={{ r: 4, fill: SHORTFALL_COLOR, stroke: '#0A1628', strokeWidth: 2 }}
              connectNulls={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* ── Rebalance count / caption ──────────────────────────────────────── */}
      {rebalanceDates.length > 0 && (
        <p className="text-xs dark:text-white/30 text-gray-400 mt-2">
          {useDots
            ? `${rebalanceDates.length} rebalances since inception`
            : 'Vertical lines mark rebalance dates'}
        </p>
      )}
      <p className="text-[10px] dark:text-white/25 text-gray-300 mt-1.5 leading-relaxed">
        {isShortfall
          ? 'Cumulative implementation shortfall in index points. Below zero means the tracking portfolio trails the index.'
          : 'The index is a frictionless, rules-based series. The tracking portfolio reflects transaction costs, management fees, T+1 settlement and phased deployment. The gap between them is the cost of implementation.'}
      </p>

      {/* ── Data table (independent toggle) ───────────────────────────────── */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs dark:text-white/30 text-gray-400">
            {tableView === 'portfolio'
              ? 'All values in index points. Costs and fees are already deducted from the index value.'
              : 'All values in index points. Index series: frictionless — no transaction costs or management fees.'}
          </p>
          <div className="flex gap-1">
            {(['portfolio', 'index'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setTableView(s)}
                className={`px-2.5 py-1 text-[10px] rounded font-semibold uppercase tracking-wide transition ${
                  tableView === s
                    ? s === 'portfolio'
                      ? 'border'
                      : 'border border-gsw-gold/40 bg-gsw-gold/10 text-gsw-gold'
                    : 'dark:text-white/40 text-gray-400 border border-transparent hover:dark:border-white/20 hover:border-gray-300'
                }`}
                style={
                  tableView === s && s === 'portfolio'
                    ? { color: PORTFOLIO_COLOR, backgroundColor: `${PORTFOLIO_COLOR}1a`, borderColor: `${PORTFOLIO_COLOR}66` }
                    : undefined
                }
              >
                {s === 'portfolio' ? 'Portfolio' : 'Index'}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border dark:border-white/10 border-gray-200">
          <div className="overflow-y-auto" style={{ maxHeight: 400 }}>
            <table className="w-full text-xs min-w-[600px]">
              <thead className="sticky top-0 dark:bg-gsw-navy bg-white border-b dark:border-white/10 border-gray-200 z-10">
                <tr>
                  {['Date', 'Index Value', 'Daily Return', 'Invested %', 'Txn Cost', 'Mgmt Fee', 'Rebalance'].map((h, i) => (
                    <th
                      key={h}
                      className={`px-3 py-2 font-semibold text-gsw-gold uppercase tracking-wide whitespace-nowrap ${i === 0 ? 'text-left' : 'text-right'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-white/10 divide-gray-100">
                {[...tableData].reverse().map((row) => {
                  const ret = row.daily_return_pct;
                  const retClass =
                    ret === null ? 'dark:text-white/30 text-gray-400'
                    : ret > 0    ? 'text-green-400'
                    : ret < 0    ? 'text-red-400'
                                 : 'dark:text-white/30 text-gray-400';
                  const retStr =
                    ret === null ? '—'
                                 : `${ret >= 0 ? '+' : ''}${ret.toFixed(3)}%`;
                  return (
                    <tr key={row.date} className="dark:hover:bg-white/5 hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 dark:text-white/70 text-gray-600 whitespace-nowrap">{fmtDate(row.date)}</td>
                      <td className="px-3 py-2 text-right font-mono dark:text-white text-gsw-navy">{row.index_value.toFixed(4)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${retClass}`}>{retStr}</td>
                      <td className="px-3 py-2 text-right font-mono dark:text-white/70 text-gray-600">{row.invested_pct.toFixed(2)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${row.txn_cost_drag === 0 ? 'dark:text-white/25 text-gray-300' : 'dark:text-white/70 text-gray-600'}`}>
                        {row.txn_cost_drag.toFixed(4)}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${row.fee_drag === 0 ? 'dark:text-white/25 text-gray-300' : 'dark:text-white/70 text-gray-600'}`}>
                        {row.fee_drag.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {row.rebalance_signal && (
                          <span className="inline-block w-2 h-2 rounded-full bg-gsw-gold" title="Rebalance" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
